import { Badge, type SemanticTone } from '@kerfjs/ui/badge';
import { Button } from '@kerfjs/ui/button';
import { List } from '@kerfjs/ui/list';
import { ListInsetControl } from '@kerfjs/ui/list-inset-control';
import { Row } from '@kerfjs/ui/row';
import { SegmentedControl } from '@kerfjs/ui/segmented-control';
import { Spacer } from '@kerfjs/ui/spacer';
import { DialogSurface } from '@kerfjs/ui/surface-scaffold';
import { Text } from '@kerfjs/ui/text';
import { TextField } from '@kerfjs/ui/text-field';
import { ValueTable, ValueTableRow } from '@kerfjs/ui/value-table';
import { type SafeHtml } from 'kerfjs';

import { BarChart } from '../../components/bar-chart.js';
import { CurrencyAmount } from '../../components/currency-amount.js';
import { PageLoading } from '../../components/page-loading.js';
import type { AiUsageSummary } from '../../data/api.js';
import { aiBudgetDialogOpen, aiSpendView, aiUsage, me } from '../../data/state.js';
import {
  AI_USAGE_FRACTION_DIGITS,
  aiUsageAnalysisLabel,
  aiUsageDayBars,
  formatAiUsageDate,
} from '../../lib/ai-usage-days.js';

export function budgetBadge(usage: AiUsageSummary): { label: string; tone: SemanticTone } {
  if (usage.softLimitUsd === null && usage.hardLimitUsd === null) return { label: 'No budget set', tone: 'warning' };
  const display = {
    ok: { label: 'Within budget', tone: 'success' },
    warning: { label: 'Near budget — soft limit reached', tone: 'warning' },
    stopped: { label: 'Hard limit reached — new analyses are queued until the budget is raised', tone: 'danger' },
  } satisfies Record<AiUsageSummary['status'], { label: string; tone: SemanticTone }>;
  return display[usage.status];
}

export function AiUsageView(): SafeHtml {
  const usage = aiUsage.value;
  const limit = (value: number | null): string => (value === null ? '' : String(value));

  return (
    <div class="ai-usage-view ai-usage-page">
      {usage === null ? (
        <PageLoading label="Loading usage" />
      ) : (
        <List gap="m">
          <Row vAlign="middle" gap="xs" wrap controlInsets="rl">
            <Badge tone={budgetBadge(usage).tone} appearance="solid" shape="pill">
              {budgetBadge(usage).label}
            </Badge>
            <strong>
              <CurrencyAmount value={usage.monthToDateUsd} currency="USD" fractionDigits={AI_USAGE_FRACTION_DIGITS} />
            </strong>
            <Text size="compact" tone="quiet">
              {usage.calls} call{usage.calls === 1 ? null : 's'} this month
            </Text>
            <Spacer flex />
            <Button tone="neutral" emphasis="primary" size="default" action="edit-ai-budget">
              Edit Budget…
            </Button>
          </Row>

          <Spacer height="l" />

          <Row hAlign="center" controlInsets="rl">
            <SegmentedControl
              id="ai-spend-view"
              label="Spend Breakdown"
              value={aiSpendView.value}
              action="ai-spend-view"
              appearance="filled"
              shape="rounded"
              layout="equal"
              choices={[
                { value: 'day', label: 'By Day' },
                { value: 'analysis', label: 'By Analysis' },
              ]}
            />
          </Row>

          {aiSpendView.value === 'day' ? (
            <List gap="m">
              <List controlInsets="rl">
                <BarChart label="AI Spend, Last 30 Days" bars={aiUsageDayBars(usage.last30Days)} />
              </List>
              {usage.byDay.length === 0 ? (
                <ListInsetControl>
                  <Text size="compact" tone="quiet">
                    No AI spend yet this month.
                  </Text>
                </ListInsetControl>
              ) : (
                <ValueTable label="AI Spend by Day">
                  {usage.byDay.map((day) => (
                    <ValueTableRow
                      label={formatAiUsageDate(day.date)}
                      value={
                        <CurrencyAmount value={day.usd} currency="USD" fractionDigits={AI_USAGE_FRACTION_DIGITS} />
                      }
                    />
                  ))}
                </ValueTable>
              )}
            </List>
          ) : (
            <List>
              {usage.byAnalysis.length === 0 ? (
                <ListInsetControl>
                  <Text size="compact" tone="quiet">
                    No analyses have run this month.
                  </Text>
                </ListInsetControl>
              ) : (
                <ValueTable label="AI Spend by Analysis">
                  {usage.byAnalysis.map((analysis) => (
                    <ValueTableRow
                      label={aiUsageAnalysisLabel(analysis)}
                      value={
                        <CurrencyAmount value={analysis.usd} currency="USD" fractionDigits={AI_USAGE_FRACTION_DIGITS} />
                      }
                    />
                  ))}
                </ValueTable>
              )}
            </List>
          )}
        </List>
      )}

      {aiBudgetDialogOpen.value && usage !== null ? (
        <DialogSurface
          label="AI Budget"
          lightDismiss
          rootAttributes={{ 'data-action': 'ai-budget-dialog', 'data-auto-open-dialog': '' }}
          footer={
            <>
              <Button
                tone="neutral"
                size="default"
                emphasis="plain"
                action="cancel-ai-budget"
                rootAttributes={{ 'data-dialog-close': '' }}
              >
                Cancel
              </Button>
              <Button size="default" tone="brand" emphasis="primary" action="save-ai-budget">
                Save Budget
              </Button>
            </>
          }
        >
          <List gap="m">
            <ListInsetControl>
              <Text size="compact" tone="quiet">
                Leave a field blank to remove that limit. At or above the hard limit, new analyses are queued, not
                cancelled, and resume when you raise the limit or the month rolls over.
              </Text>
            </ListInsetControl>
            <ListInsetControl>
              <TextField
                name="b_soft"
                label="Soft Warning Limit"
                type="number"
                min="0"
                step="1"
                value={limit(usage.softLimitUsd)}
                placeholder="none"
              />
            </ListInsetControl>
            <ListInsetControl>
              <TextField
                name="b_hard"
                label="Hard Stop Limit"
                type="number"
                min="0"
                step="1"
                value={limit(usage.hardLimitUsd)}
                placeholder="none"
              />
            </ListInsetControl>
          </List>
        </DialogSurface>
      ) : null}

      <Row hAlign="center" flex>
        <Text>
          <a href="/privacy">Privacy &amp; data</a>
          {(me.value?.roles ?? []).includes('admin') ? (
            <>
              {' · '}
              <a href="/admin">Admin console</a>
            </>
          ) : null}
        </Text>
      </Row>
    </div>
  );
}
